import QRCode from 'qrcode';
import { cfpBaseUrl } from '../../network/urls';

/** Same New Connection DN QR endpoint. */
export const MRC_BILL_QR_API_PATH =
    process.env.REACT_APP_QR_GENERATE_PATH || 'api/L3FyL2ZvckRO';

export const MRC_BILL_QR_STORAGE_KEY = 'eops:mrcBillPaymentQr';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export const parseMrcBillNumber = (value) => {
    const n = Number(String(value ?? '').replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : 0;
};

/**
 * PAYABLE AMOUNT = application payableWithinDueDate ± CP52 Adjustment (AMOUNT):
 * - AMOUNT > 0  → payableWithinDueDate + AMOUNT
 * - AMOUNT < 0  → payableWithinDueDate + AMOUNT  (minus, because AMOUNT is negative)
 * - AMOUNT = 0  → payableWithinDueDate
 * QR Amount uses absolute value (payment APIs expect non-negative).
 */
export const computeMrcBillPayableAmount = (
    payableWithinDueDate,
    cp52Amount
) => {
    const base = parseMrcBillNumber(payableWithinDueDate);
    const amount = parseMrcBillNumber(cp52Amount);
    return Math.round(base + amount);
};

export const computeMrcBillQrAmount = (
    payableWithinDueDate,
    cp52Amount
) => Math.abs(computeMrcBillPayableAmount(payableWithinDueDate, cp52Amount));

const formatQrDate = (dateString) => {
    if (!dateString) return '';
    const raw = String(dateString).trim();
    if (!raw) return '';

    const direct = raw.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
    if (direct) {
        const [, day, month, year] = direct;
        const yr = year.length === 2 ? year : year.slice(-2);
        return `${day.padStart(2, '0')}-${month.toUpperCase()}-${yr}`;
    }

    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = MONTHS[date.getMonth()];
        const year = String(date.getFullYear()).slice(-2);
        return `${day}-${month}-${year}`;
    }

    return raw.toUpperCase().replace(/\s+/g, '-');
};

const pickQrPayloadFromJson = (json) => {
    if (!json || typeof json !== 'object') return null;

    const candidates = [
        json['QR Code'],
        json['qr code'],
        json.QRCode,
        json.qrCode,
        json.qrString,
        json.QRString,
        json.raastQr,
        json.RAASTQR,
        json.data,
        json.Data,
        json.result,
        json.Result,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim();
        }
    }
    return null;
};

/** EMV / RAAST payload string → bill QR image data URL */
const toMrcBillQrDataUrl = async (payload) => {
    const cleaned = String(payload || '').trim();
    if (!cleaned) return null;

    if (cleaned.startsWith('data:image')) return cleaned;

    if (cleaned.startsWith('iVBORw0KGgo') || cleaned.startsWith('/9j/')) {
        const mime = cleaned.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
        return `data:${mime};base64,${cleaned.replace(/\s/g, '')}`;
    }

    try {
        return await QRCode.toDataURL(cleaned, {
            errorCorrectionLevel: 'M',
            margin: 0,
            width: 180,
        });
    } catch (e) {
        console.warn('[MRC Bill QR] QRCode.toDataURL failed', e);
        return null;
    }
};

/**
 * MRC bill payment QR — same API as New Connection DN.
 * PType is always "1".
 * Response "QR Code" string is rendered into a bill QR image.
 */
export const fetchMrcBillPaymentQr = async ({
    token,
    billNo,
    appNo = '',
    refNo = '',
    amount = 0,
    dueDate = '',
    bPeriod = '',
}) => {
    if (!token) {
        console.warn('[MRC Bill QR] skipped: missing token');
        return null;
    }

    const resolvedBillNo = String(billNo || appNo || 'MRC').trim();
    const payload = {
        BillNo: resolvedBillNo,
        AppNo: String(appNo || resolvedBillNo).trim(),
        RefNo: String(refNo || '').trim(),
        Amount: Number(amount) || 0,
        DueDate: formatQrDate(dueDate),
        BPeriod: formatQrDate(bPeriod) || formatQrDate(dueDate),
        PType: '1',
    };

    const url = `${cfpBaseUrl}${MRC_BILL_QR_API_PATH}`;
    console.log('[MRC Bill QR] POST', url, payload);

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        redirect: 'follow',
    });

    console.log('[MRC Bill QR] status', response.status);

    const contentType = String(response.headers.get('content-type') || '').toLowerCase();

    if (contentType.includes('image/')) {
        const blob = await response.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result || null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    }

    const text = (await response.text())?.trim();
    if (!text) return null;

    if (text.startsWith('data:image')) return text;
    if (text.startsWith('iVBORw0KGgo') || text.startsWith('/9j/')) {
        const mime = text.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
        return `data:${mime};base64,${text.replace(/\s/g, '')}`;
    }

    if (text.startsWith('{') || text.startsWith('[')) {
        try {
            const json = JSON.parse(text);
            const candidate = pickQrPayloadFromJson(json);
            if (candidate) {
                return await toMrcBillQrDataUrl(candidate);
            }
        } catch (e) {
            console.warn('[MRC Bill QR] JSON parse failed', e);
        }
        return null;
    }

    if (text.length > 20) {
        return await toMrcBillQrDataUrl(text.replace(/^"|"$/g, ''));
    }
    return null;
};

export const stashMrcBillPaymentQr = (qrPayload) => {
    try {
        if (qrPayload) {
            localStorage.setItem(MRC_BILL_QR_STORAGE_KEY, String(qrPayload));
        } else {
            localStorage.removeItem(MRC_BILL_QR_STORAGE_KEY);
        }
    } catch (e) {
        // ignore
    }
};

export const consumeStashedMrcBillPaymentQr = () => {
    try {
        const value = localStorage.getItem(MRC_BILL_QR_STORAGE_KEY);
        localStorage.removeItem(MRC_BILL_QR_STORAGE_KEY);
        return value || null;
    } catch (e) {
        return null;
    }
};
